<template>
  <WdsModal :show="isOpen" :title="$t('Request payment')" @cancel="emit('cancel')">
    <WdsSelect
      v-model="selectedJob"
      :disabled="loadingHires === true || !hires.length"
      :label="$t('Which job is this request related to?')"
      :placeholder="placeholderText"
      :list="hireOptions"
      data-test="payment-requests-modal-contract-select"
    ></WdsSelect>
    <template #footer>
      <div class="tw-flex tw-justify-between">
        <WdsButton data-test="payment-requests-modal-cancel-button" @click="emit('cancel')">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton v-if="!url" data-test="payment-requests-modal-continue-button" disabled primary>
          {{ $t('Continue') }}
        </WdsButton>
        <WdsButton
          v-if="url"
          data-test="payment-requests-modal-continue-button"
          primary
          :href="url"
        >
          {{ $t('Continue') }}
        </WdsButton>
      </div>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import moment from 'moment'
import { WdsButton, WdsModal, WdsSelect } from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref } from 'vue'
import gql from 'graphql-tag'
import { useTranslation } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'

interface Props {
  isOpen: boolean
  showWorkerName: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  showWorkerName: false,
})

const emit = defineEmits(['cancel'])

const { $t } = useTranslation()

const { result: activeHires, loading: loadingHires } = useQuery(
  gql`
    query hires($accounts: [ID!]) {
      hires(first: 100000, accounts: $accounts, status: [ACTIVE]) {
        data {
          id
          status
          company {
            name
          }
          startDate
          endDate
          conversation {
            closedAt
          }
          job {
            id
            name
          }
          worker {
            name
          }
        }
      }
    }
  `,
  { accounts: [currentAccount.globalId] },
  {
    clientId: 'publicApi',
  },
)

const hires = computed(
  () => activeHires.value?.hires?.data?.filter((hire) => !hire.conversation?.closedAt) ?? [],
)

const hireOptions = computed(() =>
  hires.value.map((hire) => ({
    value: hire.id,
    label: props.showWorkerName ? `${hire.job.name} (${hire.worker.name})` : hire.job.name,
    subLabel: $t(':company, :startDate - :endDate', {
      company: hire.company.name,
      startDate: hire.startDate ? moment(hire.startDate).format('MMM D YYYY') : $t('No start date'),
      endDate: hire.endDate ? moment(hire.endDate).format('MMM D YYYY') : $t('No end date'),
    }),
  })),
)

const selectedJob = ref<{ value: string; label: string } | null>(null)

const placeholderText = computed(() => {
  if (loadingHires.value) return $t('Loading...')
  if (!hires.value.length) return $t('No active contracts available')
  return $t('Select job')
})

const url = computed(() => {
  if (!selectedJob.value) return null

  return laravelNamedRoute(namedRoutes.bills.create, { hire: selectedJob.value.value })
})
</script>
